import { useState, type ReactNode } from 'react';
import {
  BookOutlined,
  AppstoreOutlined,
  EditOutlined,
  PlusOutlined,
  CaretDownOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import type { AppMode } from '../../store/reducer';

export interface TemplateLike {
  id: string;
  name: string;
  summary: string;
}

interface Props {
  mode: AppMode;
  current: TemplateLike | null;
  templates: TemplateLike[];
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onCreateNew: () => void;
  onOpenLibrary: () => void;
}

const modeIcon = (mode: AppMode): ReactNode =>
  mode === 'contract' ? <BookOutlined /> : <AppstoreOutlined />;

export function ActiveTemplateChip({
  mode,
  current,
  templates,
  onSelect,
  onEdit,
  onCreateNew,
  onOpenLibrary,
}: Props) {
  const [open, setOpen] = useState(false);

  if (!current) {
    return (
      <div
        style={{
          padding: '12px 14px',
          background: 'var(--surface-2)',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 7,
            background: 'var(--surface-1)',
            border: '1px dashed var(--border-strong)',
            color: 'var(--ink-3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {modeIcon(mode)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600 }}>未关联模板</div>
          <div style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>从模板库选一个或新建</div>
        </div>
        <Button size="small" onClick={onOpenLibrary}>
          模板库
        </Button>
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={onCreateNew}>
          新建
        </Button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          padding: '10px 12px',
          background: 'var(--surface-1)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          boxShadow: '0 1px 2px rgba(0,0,0,.02)',
        }}
      >
        <button
          className="pp-bare-button"
          onClick={() => setOpen(!open)}
          title="切换模板"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 7,
              background: 'var(--accent)',
              color: 'var(--accent-ink)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {modeIcon(mode)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {current.name}
              </span>
              <span
                style={{
                  color: 'var(--ink-3)',
                  display: 'flex',
                  transform: open ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform .15s',
                }}
              >
                <CaretDownOutlined style={{ fontSize: 11 }} />
              </span>
            </div>
            <div
              style={{
                fontSize: 10.5,
                color: 'var(--ink-3)',
                marginTop: 2,
                fontFamily: 'var(--font-mono)',
                letterSpacing: '-.02em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {current.summary}
            </div>
          </div>
        </button>
        <Tooltip title="编辑此模板">
          <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(current.id)}>
            编辑
          </Button>
        </Tooltip>
      </div>

      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10,
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: 4,
              background: 'var(--surface-1)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,.12)',
              padding: 4,
              zIndex: 20,
              maxHeight: 320,
              overflowY: 'auto',
            }}
            className="pp-scroll"
          >
            {templates.map((t) => {
              const isCurrent = t.id === current.id;
              return (
                <button
                  key={t.id}
                  className="pp-bare-button"
                  onClick={() => {
                    onSelect(t.id);
                    setOpen(false);
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = 'var(--hover)')
                  }
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  style={{
                    width: '100%',
                    padding: '8px 8px',
                    borderRadius: 6,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 5,
                      background: isCurrent ? 'var(--accent)' : 'var(--surface-2)',
                      color: isCurrent ? 'var(--accent-ink)' : 'var(--ink-2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: 11,
                    }}
                  >
                    {modeIcon(mode)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12.5,
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t.name}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: 'var(--ink-3)',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {t.summary}
                    </div>
                  </div>
                  {isCurrent && (
                    <span style={{ color: 'var(--accent)', flexShrink: 0 }}>
                      <CheckOutlined style={{ fontSize: 12 }} />
                    </span>
                  )}
                </button>
              );
            })}
            <div
              style={{
                borderTop: '1px solid var(--border-soft)',
                margin: 4,
              }}
            />
            <button
              className="pp-bare-button"
              onClick={() => {
                setOpen(false);
                onCreateNew();
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              style={{
                width: '100%',
                padding: 8,
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: 'var(--ink-2)',
                fontWeight: 500,
              }}
            >
              <PlusOutlined style={{ fontSize: 12 }} />
              新建模板
            </button>
            <button
              className="pp-bare-button"
              onClick={() => {
                setOpen(false);
                onOpenLibrary();
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              style={{
                width: '100%',
                padding: 8,
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: 'var(--ink-2)',
                fontWeight: 500,
              }}
            >
              <AppstoreOutlined style={{ fontSize: 12 }} />
              模板库 · 管理
            </button>
          </div>
        </>
      )}
    </div>
  );
}
